import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Select } from '@components/core-ui/form-elements/Select';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { JobsService } from 'app/API';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { createFetchJobQueryFn } from 'app/queries/jobs/createFetchJobQueryFn';
import { jobsKeys } from 'app/queries/jobs/jobsKeys';
import { createFetchAvailableLayoutsQueryFn } from 'app/queries/layouts/createFetchAvailableLayoutsQueryFn';
import { layoutsKeys } from 'app/queries/layouts/layoutsKeys';
import { useState } from 'react';

import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { Content } from './styles/Content';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
  id: string;
};

export const ChooseCloudLayoutModal = ({ id, onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const { workspace, organization } = useAuthContext();
  const queryClient = useQueryClient();
  const workspaceId = workspace?.id ?? '';
  const organizationId = organization?.id ?? '';
  const [chosenCloudLayout, setChosenCloudLayout] = useState<string | undefined>();

  const jobQuery = useQuery({
    queryKey: jobsKeys.workspaceDetail(id, workspaceId),
    queryFn: createFetchJobQueryFn(call),
    enabled: workspaceId !== '' && organizationId !== '',
  });
  const layoutsQuery = useQuery({
    queryKey: layoutsKeys.availableByOrganization(organizationId),
    queryFn: createFetchAvailableLayoutsQueryFn(call),
    enabled: workspaceId !== '' && organizationId !== '',
  });
  useNotifyQueryError(jobQuery.error);
  useNotifyQueryError(layoutsQuery.error);
  const selectedCloudLayout = chosenCloudLayout ?? (jobQuery.data?.layoutId || 'null');

  const updateLayoutMutation = useMutation({
    mutationFn: (layoutId: string | null) =>
      call(() =>
        JobsService.updateLayout({
          jobId: id,
          wsid: workspaceId,
          requestBody: { layoutId },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobsKeys.detail(id) });
      onSuccess();
    },
    onError: notifyApiCallError,
  });

  const isLoading =
    workspaceId === '' ||
    organizationId === '' ||
    jobQuery.isLoading ||
    layoutsQuery.isLoading ||
    updateLayoutMutation.isPending;

  const submit = () => {
    updateLayoutMutation.mutate(selectedCloudLayout === 'null' ? null : selectedCloudLayout);
  };

  const layoutsList = [
    {
      label: 'None',
      value: 'null',
    },
    ...(layoutsQuery.data || []).map((l) => ({
      value: l.id,
      label: l.name,
    })),
  ];

  return (
    <ModalWrapper width="487px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Insert layout</ModalTitle>

      <Content>
        <div className="description">
          You can choose a layout for this Job. It will be automatically applied to the transcript.
        </div>

        <Select
          name="layoutId"
          className="layout-select"
          label="Cloud Layout"
          value={selectedCloudLayout}
          disabled={layoutsList.length < 2 || isLoading}
          onChange={(_, value) => setChosenCloudLayout(value)}
          options={layoutsList}
          required={false}
        />

        <div className="footer-description">
          * If you don’t have a cloud layout available, please visit this page for instructions to create cloud layouts
          for use in job processing.
        </div>

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button disabled={!selectedCloudLayout.length || isLoading || jobQuery.isError} onClick={submit}>
            Apply
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
